import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonMenuButton,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonBadge,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonFab,
  IonFabButton,
  IonModal,
  IonInput,
  IonDatetime,
  IonTextarea,
  IonToast,
  IonSpinner,
  IonRefresher,
  IonRefresherContent,
  RefresherEventDetail,
  IonList,
  IonChip,
  IonActionSheet,
  IonAlert,
  IonText,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import {
  addOutline,
  filterOutline,
  downloadOutline,
  ellipsisVertical,
  createOutline,
  trashOutline,
  calendarOutline,
  locationOutline,
  pricetagOutline,
  ticketOutline,
  cloudUploadOutline,
  copyOutline,
  layersOutline,
  gridOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { useEvent } from '../contexts/EventContext';
import { ticketService } from '../services/api';
import { Ticket, Event } from '../types';
import EventSelector from '../components/EventSelector';
import EnhancedEventSelector from '../components/EnhancedEventSelector';
import EventSelectOrCreate from '../components/EventSelectOrCreate';
import CreateEventForm from '../components/CreateEventForm';
import { useLocation } from 'react-router-dom';
import CenteredLoader from '../components/CenteredLoader';
import { eventBus, EVENTS } from '../utils/eventBus';
import {
  generateInventoryTemplate,
  parseInventoryExcel,
} from '../utils/excelUtils';
import { TICKET_SITES, getDefaultSite } from '../utils/siteConstants';
import { supplierService } from '../services/api';
import { Supplier } from '../types';

const Inventory: React.FC = () => {
  const { t } = useTranslation();
  const { currentEvent, setCurrentEvent, loading: eventsLoading } = useEvent();
  const location = useLocation<{ openAddModal?: boolean }>();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterVenue, setFilterVenue] = useState('');
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [selectedEventForTicket, setSelectedEventForTicket] =
    useState<Event | null>(currentEvent);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [viewMode, setViewMode] = useState<'category' | 'block'>('category');
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [defaultSupplier, setDefaultSupplier] = useState<Supplier | null>(null);

  const [formData, setFormData] = useState<Partial<Ticket>>({
    eventName: '',
    eventDate: new Date(),
    eventTime: '',
    venue: '',
    section: '',
    row: '',
    seat: '',
    purchasePrice: 0,
    quantity: 1,
    supplier: '',
    sourceType: 'supplier',
    status: 'available',
  });
  const [sellerName, setSellerName] = useState('');

  const loadSuppliers = async () => {
    try {
      const data = await supplierService.getAllWithBalance();
      setSuppliers(data || []);
      
      // Set default supplier (first one if available)
      if (data && data.length > 0) {
        const firstSupplier = data[0];
        setDefaultSupplier(firstSupplier);
      }
    } catch (error) {
      console.error('Failed to load suppliers:', error);
      setSuppliers([]);
    }
  };

  useEffect(() => {
    if (currentEvent) {
      loadTickets();
    } else {
      // If no event is selected, stop loading
      setLoading(false);
      setTickets([]);
      setFilteredTickets([]);
    }
  }, [currentEvent]);

  useEffect(() => {
    loadSuppliers();
  }, []);

  useEffect(() => {
    // Update form data when default supplier is loaded
    if (defaultSupplier && !formData.supplier) {
      setFormData(prev => ({
        ...prev,
        supplier: defaultSupplier.name,
        sourceType: 'supplier'
      }));
    }
  }, [defaultSupplier]);

  useEffect(() => {
    filterTicketsList();
  }, [tickets, searchText, filterVenue]);

  useEffect(() => {
    // Subscribe to event bus for refreshing tickets
    const unsubscribeTickets = eventBus.on(EVENTS.REFRESH_TICKETS, () => {
      console.log('Inventory: Refreshing tickets due to email fetch');
      if (currentEvent) {
        loadTickets();
      }
    });

    const unsubscribeSalesCreated = eventBus.on(
      EVENTS.SALES_CREATED_FROM_EMAIL,
      (data: { count: number }) => {
        setToastMessage(
          `${data.count} sale${
            data.count > 1 ? 's' : ''
          } created from emails. Tickets updated.`
        );
        setShowToast(true);
      }
    );

    // Cleanup subscriptions on unmount
    return () => {
      unsubscribeTickets();
      unsubscribeSalesCreated();
    };
  }, [currentEvent]);

  useEffect(() => {
    // Check if we should open the add modal from navigation state
    if (location.state?.openAddModal) {
      // Pre-populate form with current event data
      if (currentEvent) {
        setFormData((prev) => ({
          ...prev,
          eventName: currentEvent.name,
          eventDate: new Date(currentEvent.date),
          eventTime: currentEvent.time || '',
          venue: currentEvent.venue,
        }));
      }
      setShowAddModal(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location, currentEvent]);

  const loadTickets = async () => {
    if (!currentEvent) return;

    try {
      setError(null);
      setLoading(true);
      const data = await ticketService.getAll({ eventId: currentEvent._id });
      setTickets(data || []);
    } catch (err: any) {
      setError(err.message || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const filterTicketsList = () => {
    let filtered = [...tickets];

    if (searchText) {
      filtered = filtered.filter(
        (ticket) =>
          ticket.eventName.toLowerCase().includes(searchText.toLowerCase()) ||
          ticket.venue.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    if (filterVenue) {
      filtered = filtered.filter((ticket) => ticket.venue === filterVenue);
    }

    setFilteredTickets(filtered);
  };

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await loadTickets();
    event.detail.complete();
  };

  const handleAddTicket = async () => {
    if (!selectedEventForTicket) return;

    // Validate seller name for private purchases
    if (formData.sourceType === 'private' && !sellerName.trim()) {
      setToastMessage('Seller name is required for private purchases');
      setShowToast(true);
      return;
    }

    // Validate supplier selection for supplier purchases
    if (formData.sourceType === 'supplier' && !formData.supplier) {
      setToastMessage('Supplier selection is required');
      setShowToast(true);
      return;
    }

    // Validate site selection for site purchases
    if (formData.sourceType === 'site' && !formData.supplier) {
      setToastMessage('Purchase source selection is required');
      setShowToast(true);
      return;
    }

    try {
      // Only send ticket-specific data and eventId
      // The backend will populate event details automatically
      const ticketData = {
        eventId: selectedEventForTicket._id,
        section: formData.section,
        row: formData.row,
        seat: formData.seat,
        purchasePrice: formData.purchasePrice,
        quantity: formData.quantity,
        sourceType: formData.sourceType,
        supplier: formData.sourceType === 'private' ? sellerName.trim() : formData.supplier,
        status: formData.status,
      };

      await ticketService.create(ticketData as Omit<Ticket, 'id'>);
      setToastMessage(t('inventory.addTicketSuccess'));
      setShowToast(true);
      setShowAddModal(false);
      resetForm();
      setSelectedEventForTicket(currentEvent); // Reset to current event
      loadTickets();
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const handleEditTicket = async () => {
    if (!selectedTicket) return;

    // Validate seller name for private purchases
    if (formData.sourceType === 'private' && !sellerName.trim()) {
      setToastMessage('Seller name is required for private purchases');
      setShowToast(true);
      return;
    }

    // Validate supplier selection for supplier purchases
    if (formData.sourceType === 'supplier' && !formData.supplier) {
      setToastMessage('Supplier selection is required');
      setShowToast(true);
      return;
    }

    // Validate site selection for site purchases
    if (formData.sourceType === 'site' && !formData.supplier) {
      setToastMessage('Purchase source selection is required');
      setShowToast(true);
      return;
    }

    try {
      // Only send updatable fields
      const updateData = {
        section: formData.section,
        row: formData.row,
        seat: formData.seat,
        purchasePrice: formData.purchasePrice,
        quantity: formData.quantity,
        sourceType: formData.sourceType,
        supplier: formData.sourceType === 'private' ? sellerName.trim() : formData.supplier,
        status: formData.status,
      };
      await ticketService.update(selectedTicket.id!, updateData);
      setToastMessage(t('inventory.updateTicketSuccess'));
      setShowToast(true);
      setShowEditModal(false);
      resetForm();
      loadTickets();
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const handleDeleteTicket = async () => {
    if (!ticketToDelete) return;

    try {
      await ticketService.delete(ticketToDelete.id!);
      setToastMessage(t('inventory.deleteTicketSuccess'));
      setShowToast(true);
      setShowDeleteAlert(false);
      setTicketToDelete(null);
      loadTickets();
    } catch (err: any) {
      setToastMessage(err.message || t('errors.generic'));
      setShowToast(true);
    }
  };

  const resetForm = () => {
    const getDefaultSupplierValue = () => {
      if (defaultSupplier) {
        return defaultSupplier.name;
      }
      return getDefaultSite().displayName;
    };

    setFormData({
      eventName: currentEvent?.name || '',
      eventDate: currentEvent ? new Date(currentEvent.date) : new Date(),
      eventTime: currentEvent?.time || '',
      venue: currentEvent?.venue || '',
      section: '',
      row: '',
      seat: '',
      purchasePrice: 0,
      quantity: 1,
      supplier: getDefaultSupplierValue(),
      sourceType: defaultSupplier ? 'supplier' : 'site',
      status: 'available',
    });
    setSellerName('');
    setSelectedTicket(null);
  };

  const openEditModal = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setFormData(ticket);
    // If it's a private purchase, extract seller name from supplier field
    if (ticket.sourceType === 'private') {
      setSellerName(ticket.supplier || '');
    } else {
      setSellerName('');
    }
    setShowEditModal(true);
    setShowActionSheet(false);
  };

  const openDeleteAlert = (ticket: Ticket) => {
    setTicketToDelete(ticket);
    setShowDeleteAlert(true);
    setShowActionSheet(false);
  };

  const handleDownloadTemplate = () => {
    try {
      generateInventoryTemplate();
      setToastMessage('Inventory template downloaded successfully');
      setShowToast(true);
    } catch (error) {
      console.error('Error downloading template:', error);
      setToastMessage('Failed to download template');
      setShowToast(true);
    }
  };

  const handleImportExcel = async () => {
    if (!importFile) {
      setToastMessage('Please select a file to import');
      setShowToast(true);
      return;
    }

    if (!currentEvent) {
      setToastMessage('Please select an event first');
      setShowToast(true);
      return;
    }

    setImporting(true);
    try {
      // Parse the Excel file
      const parsedInventory = await parseInventoryExcel(importFile, [
        currentEvent,
      ]);

      // Import each ticket
      let successCount = 0;
      let errorCount = 0;

      for (const ticketData of parsedInventory) {
        try {
          await ticketService.create({
            ...ticketData,
            eventId: currentEvent._id,
          } as Ticket);
          successCount++;
        } catch (error) {
          console.error('Error importing ticket:', error);
          errorCount++;
        }
      }

      setToastMessage(
        `Imported ${successCount} tickets successfully${
          errorCount > 0 ? `, ${errorCount} failed` : ''
        }`
      );
      setShowToast(true);

      // Reload tickets
      await loadTickets();
      setShowImportModal(false);
      setImportFile(null);
    } catch (error: any) {
      console.error('Error importing Excel:', error);
      setToastMessage(error.message || 'Failed to import Excel file');
      setShowToast(true);
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'sold':
        return 'danger';
      case 'reserved':
        return 'warning';
      default:
        return 'medium';
    }
  };

  const copyInventorySummary = async () => {
    try {
      const inventoryByCategory: {
        [key: string]: { available: number; sold: number };
      } = {};

      // Group tickets by category/block and status
      filteredTickets.forEach((ticket) => {
        let key: string;
        if (viewMode === 'category') {
          key = ticket.category || 'No Category';
        } else {
          // Block view - include block in the key
          key = ticket.block
            ? `${ticket.category || 'No Category'} - ${ticket.block}`
            : ticket.category || 'No Category';
        }

        if (!inventoryByCategory[key]) {
          inventoryByCategory[key] = { available: 0, sold: 0 };
        }

        if (ticket.status === 'available') {
          inventoryByCategory[key].available += ticket.quantity;
        } else if (ticket.status === 'sold') {
          inventoryByCategory[key].sold += ticket.quantity;
        }
      });

      let leftToSellItems: string[] = [];
      let soldItems: string[] = [];

      Object.entries(inventoryByCategory).forEach(
        ([category, { available, sold }]) => {
          if (available > 0) {
            leftToSellItems.push(`${available} ${category}`);
          }
          if (sold > 0) {
            soldItems.push(`${sold} ${category}`);
          }
        }
      );

      const summary = [
        'left to sell',
        '',
        ...leftToSellItems,
        '',
        'sold inventory',
        '',
        ...soldItems,
      ].join('\n');

      await navigator.clipboard.writeText(summary);
      setToastMessage('Inventory summary copied to clipboard');
      setShowToast(true);
    } catch (err) {
      console.error('Failed to copy inventory summary:', err);
      setToastMessage('Failed to copy inventory summary');
      setShowToast(true);
    }
  };

  const uniqueVenues = Array.from(
    new Set(Array.isArray(tickets) ? tickets.map((t) => t.venue) : [])
  );

  if (loading || eventsLoading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonMenuButton menu="main-menu" />
            </IonButtons>
            <IonTitle>{t('inventory.title')}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <CenteredLoader
            message={t('inventory.loadingTickets') || 'Loading tickets...'}
          />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton menu="main-menu" />
          </IonButtons>
          <IonTitle>{t('inventory.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={handleDownloadTemplate}
              title="Download Excel Template"
            >
              <IonIcon icon={downloadOutline} />
            </IonButton>
            <IonButton
              onClick={() => setShowImportModal(true)}
              title="Import Excel"
            >
              <IonIcon icon={cloudUploadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar>
          <IonSearchbar
            value={searchText}
            onIonChange={(e) => setSearchText(e.detail.value!)}
            placeholder={t('common.search')}
            className="px-2"
          />
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="bg-gray-50 dark:bg-gray-900">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent></IonRefresherContent>
        </IonRefresher>

        <IonGrid className="p-4">
          <IonRow className="mb-4">
            <IonCol size="12">
              <EnhancedEventSelector
                currentEvent={currentEvent}
                onEventSelect={setCurrentEvent}
                onCreateEvent={() => setShowCreateEventModal(true)}
                showAllEvents={true}
              />
            </IonCol>
          </IonRow>

          <IonRow>
            <IonCol size="12" sizeMd="3">
              <IonSelect
                value={filterVenue}
                onIonChange={(e) => setFilterVenue(e.detail.value)}
                placeholder={t('inventory.filterByVenue')}
                interface="popover"
                className="w-full"
              >
                <IonSelectOption value="">
                  {t('inventory.filterByVenue')}
                </IonSelectOption>
                {uniqueVenues.map((venue) => (
                  <IonSelectOption key={venue} value={venue}>
                    {venue}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonCol>
          </IonRow>

          {/* Inventory Summary Card */}
          {filteredTickets.length > 0 && (
            <IonRow className="mt-4">
              <IonCol size="12">
                <IonCard className="shadow-lg">
                  <IonCardHeader className="bg-primary-50 dark:bg-primary-900/30">
                    <div className="flex items-center justify-between">
                      <IonCardTitle className="text-lg flex items-center">
                        <IonIcon icon={ticketOutline} className="mr-2" />
                        Inventory Summary
                      </IonCardTitle>
                      <div className="flex items-center gap-2">
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={copyInventorySummary}
                          title="Copy inventory summary"
                        >
                          <IonIcon icon={copyOutline} />
                        </IonButton>
                        <IonSegment
                          value={viewMode}
                          onIonChange={(e) =>
                            setViewMode(e.detail.value as 'category' | 'block')
                          }
                          className="w-auto"
                          style={{ maxWidth: '280px' }}
                        >
                          <IonSegmentButton value="category">
                            <IonLabel className="text-xs">
                              <IonIcon icon={layersOutline} className="mr-1" />
                              Category
                            </IonLabel>
                          </IonSegmentButton>
                          <IonSegmentButton value="block">
                            <IonLabel className="text-xs">
                              <IonIcon icon={gridOutline} className="mr-1" />
                              Block
                            </IonLabel>
                          </IonSegmentButton>
                        </IonSegment>
                      </div>
                    </div>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      <p>
                        <strong>Available:</strong>{' '}
                        {filteredTickets
                          .filter((t) => t.status === 'available')
                          .reduce((sum, t) => sum + t.quantity, 0)}{' '}
                        tickets
                      </p>
                      <p>
                        <strong>Sold:</strong>{' '}
                        {filteredTickets
                          .filter((t) => t.status === 'sold')
                          .reduce((sum, t) => sum + t.quantity, 0)}{' '}
                        tickets
                      </p>
                      <p>
                        <strong>Reserved:</strong>{' '}
                        {filteredTickets
                          .filter((t) => t.status === 'reserved')
                          .reduce((sum, t) => sum + t.quantity, 0)}{' '}
                        tickets
                      </p>
                    </div>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          )}

          <IonRow className="mt-4">
            <IonCol size="12">
              {filteredTickets.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 dark:text-gray-400">
                    {t('inventory.noTickets')}
                  </p>
                </div>
              ) : (
                <IonCard className="shadow-lg">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.eventName')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.eventDate')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.venue')}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.section')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.quantity')}
                          </th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.purchasePrice')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('inventory.status')}
                          </th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            {t('common.actions')}
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredTickets.map((ticket, index) => (
                          <tr
                            key={ticket.id || ticket._id || index}
                            className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">
                                {ticket.eventName}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {format(
                                  new Date(ticket.eventDate),
                                  'MMM dd, yyyy'
                                )}
                                {ticket.eventTime && (
                                  <span className="text-gray-500 dark:text-gray-400 ml-1">
                                    {ticket.eventTime}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {ticket.venue}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {ticket.section || '-'}
                                {ticket.row && ` Row ${ticket.row}`}
                                {ticket.seat && ` Seat ${ticket.seat}`}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {ticket.quantity}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                ${ticket.purchasePrice.toFixed(2)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  ticket.status === 'available'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                    : ticket.status === 'sold'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                }`}
                              >
                                {t(`inventory.${ticket.status}`)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-center">
                              <IonButton
                                fill="clear"
                                size="small"
                                onClick={() => {
                                  setSelectedTicket(ticket);
                                  setShowActionSheet(true);
                                }}
                              >
                                <IonIcon icon={ellipsisVertical} />
                              </IonButton>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </IonCard>
              )}
            </IonCol>
          </IonRow>
        </IonGrid>

        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton
            onClick={() => {
              setSelectedEventForTicket(currentEvent);
              setShowAddModal(true);
            }}
          >
            <IonIcon icon={addOutline} />
          </IonFabButton>
        </IonFab>

        {/* Add Ticket Modal */}
        <IonModal
          isOpen={showAddModal}
          onDidDismiss={() => setShowAddModal(false)}
          className="event-modal-fullscreen"
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('inventory.addTicket')}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowAddModal(false)}>
                  {t('common.cancel')}
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton
                  strong
                  onClick={handleAddTicket}
                  disabled={
                    !selectedEventForTicket || 
                    (formData.sourceType === 'private' && !sellerName.trim()) ||
                    !formData.supplier
                  }
                >
                  {t('common.save')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="p-4">
            {/* Event Selection Section */}
            <div className="mb-4">
              <h3 className="text-lg font-semibold mb-3">
                {t('events.selectEvent')}
              </h3>
              <EventSelectOrCreate
                selectedEvent={selectedEventForTicket}
                onEventSelect={(event) => {
                  setSelectedEventForTicket(event);
                  setFormData({
                    ...formData,
                    eventName: event.name,
                    eventDate: new Date(event.date),
                    eventTime: event.time || '',
                    venue: event.venue,
                  });
                }}
              />
            </div>

            {selectedEventForTicket && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold mb-3">
                  {t('inventory.ticketDetails')}
                </h3>
                <IonList className="space-y-2">
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      {t('inventory.section')}
                    </IonLabel>
                    <IonInput
                      value={formData.section}
                      onIonChange={(e) =>
                        setFormData({ ...formData, section: e.detail.value! })
                      }
                      className="text-lg"
                    />
                  </IonItem>
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      {t('inventory.row')}
                    </IonLabel>
                    <IonInput
                      value={formData.row}
                      onIonChange={(e) =>
                        setFormData({ ...formData, row: e.detail.value! })
                      }
                      className="text-lg"
                    />
                  </IonItem>
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      {t('inventory.seat')}
                    </IonLabel>
                    <IonInput
                      value={formData.seat}
                      onIonChange={(e) =>
                        setFormData({ ...formData, seat: e.detail.value! })
                      }
                      className="text-lg"
                    />
                  </IonItem>
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      {t('inventory.purchasePrice')}
                    </IonLabel>
                    <IonInput
                      type="number"
                      value={formData.purchasePrice}
                      onIonChange={(e) =>
                        setFormData({
                          ...formData,
                          purchasePrice: parseFloat(e.detail.value!),
                        })
                      }
                      required
                      className="text-lg"
                    />
                  </IonItem>
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      {t('inventory.quantity')}
                    </IonLabel>
                    <IonInput
                      type="number"
                      value={formData.quantity}
                      onIonChange={(e) =>
                        setFormData({
                          ...formData,
                          quantity: parseInt(e.detail.value!),
                        })
                      }
                      required
                      className="text-lg"
                    />
                  </IonItem>
                  <IonItem className="rounded-lg">
                    <IonLabel position="stacked" className="pb-2">
                      Purchase Type
                    </IonLabel>
                    <IonSelect
                      value={formData.sourceType}
                      onIonChange={(e) => {
                        const sourceType = e.detail.value;
                        setFormData({ 
                          ...formData, 
                          sourceType,
                          supplier: sourceType === 'site' ? getDefaultSite().displayName : ''
                        });
                        if (sourceType !== 'private') {
                          setSellerName('');
                        }
                      }}
                      interface="popover"
                      className="text-lg"
                    >
                      <IonSelectOption value="site">Website/Platform</IonSelectOption>
                      <IonSelectOption value="private">Private Seller</IonSelectOption>
                      <IonSelectOption value="supplier">Supplier</IonSelectOption>
                    </IonSelect>
                  </IonItem>
                  
                  {formData.sourceType === 'private' ? (
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        Seller Name *
                      </IonLabel>
                      <IonInput
                        value={sellerName}
                        onIonChange={(e) => setSellerName(e.detail.value!)}
                        placeholder="Enter seller name"
                        required
                        className="text-lg"
                      />
                    </IonItem>
                  ) : (
                    <IonItem className="rounded-lg">
                      <IonLabel position="stacked" className="pb-2">
                        {formData.sourceType === 'site' ? 'Purchase Source' : 'Supplier'}
                      </IonLabel>
                      <IonSelect
                        value={formData.supplier}
                        onIonChange={(e) =>
                          setFormData({ ...formData, supplier: e.detail.value })
                        }
                        placeholder={formData.sourceType === 'site' ? 'Select purchase source' : 'Select supplier'}
                        interface="popover"
                        className="text-lg"
                      >
                        {formData.sourceType === 'site' ? (
                          TICKET_SITES.map((site) => (
                            <IonSelectOption key={site.id} value={site.displayName}>
                              {site.displayName}
                            </IonSelectOption>
                          ))
                        ) : (
                          suppliers.map((supplier) => (
                            <IonSelectOption key={supplier._id || supplier.id} value={supplier.name}>
                              {supplier.name}
                            </IonSelectOption>
                          ))
                        )}
                      </IonSelect>
                    </IonItem>
                  )}
                </IonList>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Edit Modal - Similar to Add Modal */}
        <IonModal
          isOpen={showEditModal}
          onDidDismiss={() => setShowEditModal(false)}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('inventory.editTicket')}</IonTitle>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowEditModal(false)}>
                  {t('common.cancel')}
                </IonButton>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton 
                  strong 
                  onClick={handleEditTicket}
                  disabled={
                    (formData.sourceType === 'private' && !sellerName.trim()) ||
                    !formData.supplier
                  }
                >
                  {t('common.save')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="p-4">
            {selectedTicket && (
              <IonCard className="mb-4 bg-primary-50 dark:bg-primary-900/20">
                <IonCardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-2">
                    {selectedTicket.eventName}
                  </h3>
                  <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    <p>
                      {format(
                        new Date(selectedTicket.eventDate),
                        'MMM dd, yyyy'
                      )}{' '}
                      {selectedTicket.eventTime &&
                        `• ${selectedTicket.eventTime}`}
                    </p>
                    <p>{selectedTicket.venue}</p>
                  </div>
                </IonCardContent>
              </IonCard>
            )}
            <IonList className="space-y-2">
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.section')}
                </IonLabel>
                <IonInput
                  value={formData.section}
                  onIonChange={(e) =>
                    setFormData({ ...formData, section: e.detail.value! })
                  }
                  className="text-lg"
                />
              </IonItem>
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.row')}
                </IonLabel>
                <IonInput
                  value={formData.row}
                  onIonChange={(e) =>
                    setFormData({ ...formData, row: e.detail.value! })
                  }
                  className="text-lg"
                />
              </IonItem>
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.seat')}
                </IonLabel>
                <IonInput
                  value={formData.seat}
                  onIonChange={(e) =>
                    setFormData({ ...formData, seat: e.detail.value! })
                  }
                  className="text-lg"
                />
              </IonItem>
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.purchasePrice')}
                </IonLabel>
                <IonInput
                  type="number"
                  value={formData.purchasePrice}
                  onIonChange={(e) =>
                    setFormData({
                      ...formData,
                      purchasePrice: parseFloat(e.detail.value!),
                    })
                  }
                  required
                  className="text-lg"
                />
              </IonItem>
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.quantity')}
                </IonLabel>
                <IonInput
                  type="number"
                  value={formData.quantity}
                  onIonChange={(e) =>
                    setFormData({
                      ...formData,
                      quantity: parseInt(e.detail.value!),
                    })
                  }
                  required
                  className="text-lg"
                />
              </IonItem>
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  Purchase Type
                </IonLabel>
                <IonSelect
                  value={formData.sourceType}
                  onIonChange={(e) => {
                    const sourceType = e.detail.value;
                    setFormData({ 
                      ...formData, 
                      sourceType,
                      supplier: sourceType === 'site' ? getDefaultSite().displayName : formData.supplier
                    });
                    if (sourceType !== 'private') {
                      setSellerName('');
                    }
                  }}
                  interface="popover"
                  className="text-lg"
                >
                  <IonSelectOption value="site">Website/Platform</IonSelectOption>
                  <IonSelectOption value="private">Private Seller</IonSelectOption>
                  <IonSelectOption value="supplier">Supplier</IonSelectOption>
                </IonSelect>
              </IonItem>
              
              {formData.sourceType === 'private' ? (
                <IonItem className="rounded-lg">
                  <IonLabel position="stacked" className="pb-2">
                    Seller Name *
                  </IonLabel>
                  <IonInput
                    value={sellerName}
                    onIonChange={(e) => setSellerName(e.detail.value!)}
                    placeholder="Enter seller name"
                    required
                    className="text-lg"
                  />
                </IonItem>
              ) : (
                <IonItem className="rounded-lg">
                  <IonLabel position="stacked" className="pb-2">
                    {formData.sourceType === 'site' ? 'Purchase Source' : 'Supplier'}
                  </IonLabel>
                  <IonSelect
                    value={formData.supplier}
                    onIonChange={(e) =>
                      setFormData({ ...formData, supplier: e.detail.value })
                    }
                    placeholder={formData.sourceType === 'site' ? 'Select purchase source' : 'Select supplier'}
                    interface="popover"
                    className="text-lg"
                  >
                    {formData.sourceType === 'site' ? (
                      TICKET_SITES.map((site) => (
                        <IonSelectOption key={site.id} value={site.displayName}>
                          {site.displayName}
                        </IonSelectOption>
                      ))
                    ) : (
                      suppliers.map((supplier) => (
                        <IonSelectOption key={supplier._id || supplier.id} value={supplier.name}>
                          {supplier.name}
                        </IonSelectOption>
                      ))
                    )}
                  </IonSelect>
                </IonItem>
              )}
              <IonItem className="rounded-lg">
                <IonLabel position="stacked" className="pb-2">
                  {t('inventory.status')}
                </IonLabel>
                <IonSelect
                  value={formData.status}
                  onIonChange={(e) =>
                    setFormData({ ...formData, status: e.detail.value })
                  }
                >
                  <IonSelectOption value="available">
                    {t('inventory.available')}
                  </IonSelectOption>
                  <IonSelectOption value="sold">
                    {t('inventory.sold')}
                  </IonSelectOption>
                  <IonSelectOption value="reserved">
                    {t('inventory.reserved')}
                  </IonSelectOption>
                </IonSelect>
              </IonItem>
            </IonList>
          </IonContent>
        </IonModal>

        {/* Action Sheet */}
        <IonActionSheet
          isOpen={showActionSheet}
          onDidDismiss={() => setShowActionSheet(false)}
          buttons={[
            {
              text: t('common.edit'),
              icon: createOutline,
              handler: () => {
                if (selectedTicket) openEditModal(selectedTicket);
              },
            },
            {
              text: t('common.delete'),
              icon: trashOutline,
              role: 'destructive',
              handler: () => {
                if (selectedTicket) openDeleteAlert(selectedTicket);
              },
            },
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
          ]}
        />

        {/* Delete Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header={t('common.confirm')}
          message={t('inventory.deleteTicketConfirm')}
          buttons={[
            {
              text: t('common.cancel'),
              role: 'cancel',
            },
            {
              text: t('common.delete'),
              handler: handleDeleteTicket,
            },
          ]}
        />

        {/* Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
        />

        {/* Import Excel Modal */}
        <IonModal
          isOpen={showImportModal}
          onDidDismiss={() => {
            setShowImportModal(false);
            setImportFile(null);
          }}
        >
          <IonHeader>
            <IonToolbar>
              <IonTitle>Import Inventory from Excel</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowImportModal(false)}>
                  Cancel
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonCard>
              <IonCardHeader>
                <IonCardTitle>Upload Excel File</IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div className="space-y-4">
                  <IonText>
                    <p className="text-sm text-gray-600">
                      Please upload an Excel file using the template format.
                      Download the template first to see the required columns.
                    </p>
                  </IonText>

                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setImportFile(file);
                      }
                    }}
                    className="w-full p-2 border rounded"
                  />

                  {importFile && (
                    <IonText color="medium">
                      <p className="text-sm">Selected: {importFile.name}</p>
                    </IonText>
                  )}

                  <div className="flex gap-2 mt-4">
                    <IonButton
                      expand="block"
                      onClick={handleImportExcel}
                      disabled={!importFile || importing}
                      className="flex-1"
                    >
                      {importing ? (
                        <>
                          <IonSpinner name="crescent" className="mr-2" />
                          Importing...
                        </>
                      ) : (
                        'Import Inventory'
                      )}
                    </IonButton>

                    <IonButton
                      expand="block"
                      fill="outline"
                      onClick={handleDownloadTemplate}
                      className="flex-1"
                    >
                      <IonIcon icon={downloadOutline} slot="start" />
                      Download Template
                    </IonButton>
                  </div>

                  <IonText color="medium">
                    <p className="text-xs mt-2">
                      Note: Make sure the Event Name in your Excel file matches
                      the selected event.
                    </p>
                  </IonText>
                </div>
              </IonCardContent>
            </IonCard>
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default Inventory;
